import {
  Box,
  Grid,
  Typography,
  useTheme
} from '@mui/material'

import React from 'react'

interface ComparisonProps {
  title: string;
  candidates: {
    name: string;
    color: string;
    votes: number;
    projectedVotes?: number;
  }[];
  undecidedVotes: number;
  projection?: {
    candidates: {
      name: string;
      color: string;
      votes: number;
      projectedVotes?: number;
    }[];
    undecidedVotes: number;
  };
  onClick?: () => void;
}

const Comparison: React.FC<ComparisonProps> = ({ 
  title, 
  candidates, 
  undecidedVotes, 
  projection,
  onClick 
}) => {
  const theme = useTheme();

  // Ensure we have at least 2 candidates
  if (candidates.length < 2) {
    return (
      <Box 
        sx={{ 
          backgroundColor: 'background.paper', 
          boxShadow: theme.shadows[2], 
          borderRadius: 2, 
          p: 3,
        }}
      >
        <Typography variant="body1" align="center">
          Not enough candidates for comparison
        </Typography>
      </Box>
    );
  }

  // Calculate total votes and percentages
  const totalVotes = candidates.reduce((sum, candidate) => sum + candidate.votes, 0) + undecidedVotes;
  const candidatePercentages = candidates.map(candidate => 
    candidate.votes ? (candidate.votes / totalVotes) * 100 : 0
  );
  const undecidedPercentage = undecidedVotes ? (undecidedVotes / totalVotes) * 100 : 0;

  // Projection calculations
  let projectionTotalVotes: number | undefined;
  let projectionCandidatePercentages: number[] | undefined;
  let projectionUndecidedPercentage: number | undefined;

  if (projection) {
    projectionTotalVotes = projection.candidates.reduce((sum, candidate) => sum + (candidate.projectedVotes ?? 0), 0) + projection.undecidedVotes;
    projectionCandidatePercentages = projection.candidates.map(candidate => 
      candidate.projectedVotes && projectionTotalVotes 
        ? (candidate.projectedVotes / projectionTotalVotes) * 100 
        : 0
    );
    projectionUndecidedPercentage = projectionTotalVotes 
      ? (projection.undecidedVotes / projectionTotalVotes) * 100 
      : 0;
  }

  // Determine leading candidate
  const sortedCandidates = [...candidates].sort((a, b) => b.votes - a.votes);
  const leadingCandidate = sortedCandidates[0];

  return (
    <Box 
      onClick={onClick} 
      sx={{ 
        backgroundColor: 'background.paper', 
        boxShadow: theme.shadows[2], 
        borderRadius: 2, 
        p: 3,
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s',
        '&:hover': onClick ? {
          transform: 'scale(1.02)',
        } : {}
      }}
    >
      <Typography 
        variant="h6" 
        align="center" 
        gutterBottom
      >
        {title}
      </Typography>
      
      {/* Candidate Details Grid */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        {candidates.map((candidate, index) => (
          <Grid item xs={12 / candidates.length} key={`${title}-${candidate.name}-${index}`}>
            <Box 
              sx={{ 
                p: 2, 
                backgroundColor: candidate.color + '20', 
                borderRadius: 1,
                border: candidate === leadingCandidate ? 2 : 0,
                borderColor: candidate.color
              }}
            >
              <Grid container>
                <Grid item xs={6}>
                  <Typography 
                    variant="subtitle1" 
                    fontWeight={candidate === leadingCandidate ? 'bold' : 'normal'}
                  >
                    {candidate.name}
                  </Typography>
                </Grid>
                <Grid item xs={6} textAlign="right">
                  <Typography variant="body2">
                    {candidate.votes.toLocaleString()} votes ({(candidate.votes / totalVotes * 100).toFixed(2)}%)
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Current Bar Graph */}
      <>
        <Typography 
          variant="subtitle2" 
          align="center" 
          sx={{ mt: 2, mb: 1 }}
        >
          Actual Polls
        </Typography>
        <Box 
          sx={{ 
            width: '100%', 
            height: 30, 
            display: 'flex', 
            mb: 2,
            borderRadius: 15,
            overflow: 'hidden'
          }}
        >
          {candidates.map((candidate, index) => (
            <>
              <Box 
                key={`${title}-${candidate.name}-${index}-bar`}
                sx={{ 
                  width: `${candidatePercentages[index]}%`, 
                  backgroundColor: candidate.color,
                  height: '100%'
                }} 
              />
              {candidates.length === 2 && index === 0 && (
                <Box 
                  key={`${title}-${candidate.name}-${index}-undecidedBar`}
                  sx={{ 
                    width: `${undecidedPercentage}%`, 
                    backgroundColor: 'grey.500',
                    height: '100%'
                  }} 
                />
              )}
            </>
          ))}
          {candidates.length > 2 && (
            <Box 
              sx={{ 
                width: `${undecidedPercentage}%`, 
                backgroundColor: 'grey.500',
                height: '100%'
              }} 
            />
          )}
        </Box>
      </>

      {/* Projection Bar Graph (if available) */}
      {projection && (
        <>
          <Typography 
            variant="subtitle2" 
            align="center" 
            sx={{ mt: 2, mb: 1 }}
          >
            Projected Results
          </Typography>
          <Box 
            sx={{ 
              width: '100%', 
              height: 30, 
              display: 'flex', 
              mb: 2,
              borderRadius: 15,
              overflow: 'hidden',
              opacity: 0.7
            }}
          >
            {projection.candidates.map((candidate, index) => (
              <>
                <Box 
                  key={`${title}-projection-${candidate.name}-${index}-bar`}
                  sx={{ 
                    width: `${projectionCandidatePercentages?.[index] ?? 0}%`, 
                    backgroundColor: candidate.color,
                    height: '100%'
                  }} 
                />
                {projection.candidates.length === 2 && index === 0 && (
                  <Box 
                    key={`${title}-projection-${candidate.name}-${index}-undecidedBar`}
                    sx={{ 
                      width: `${projectionUndecidedPercentage ?? 0}%`, 
                      backgroundColor: 'grey.500',
                      height: '100%'
                    }} 
                  />
                )}
              </>
            ))}
            {projection.candidates.length > 2 && (
              <Box 
                sx={{ 
                  width: `${projectionUndecidedPercentage ?? 0}%`, 
                  backgroundColor: 'grey.500',
                  height: '100%'
                }} 
              />
            )}
          </Box>
        </>
      )}

      <Grid container sx={{ color: 'text.secondary' }}>
        {candidates.map((candidate, index) => (
          <>
            <Grid 
              item 
              xs={candidates.length === 2 ? 4 : 12 / candidates.length} 
              key={`${title}-${candidate.name}-${index}-details`}
              textAlign={candidates.length > 2 ? 'center' : index === 0 ? 'left' : 'right'}
            >
              <Typography variant="body2">
                <Box component="span" sx={{ color: candidate.color, mr: 0.5 }}>■</Box>
                {candidate.name}
              </Typography>
              {projection && projectionCandidatePercentages && (
                <Typography 
                  variant="caption" 
                  sx={{ color: 'text.secondary', fontStyle: 'italic', display: 'block' }}
                >
                  Proj: {projection.candidates[index].projectedVotes} ({projectionCandidatePercentages[index].toFixed(1)}%)
                </Typography>
              )}
            </Grid>
            {candidates.length === 2 && index === 0 && (
              <Grid item xs={4} textAlign="center" key={`${title}-${candidate.name}-${index}-undecidedVotes`}>
                <Typography variant="body2">
                  <Box component="span" sx={{ color: 'grey.500', mr: 0.5 }}>■</Box>
                  Undecided: {undecidedVotes} ({undecidedPercentage.toFixed(1)}%)
                </Typography>
                {projection && projectionUndecidedPercentage !== undefined && (
                  <Typography 
                    component="span" 
                    variant="caption" 
                    sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic' }}
                  >
                    Proj: {projection.undecidedVotes} ({projectionUndecidedPercentage.toFixed(1)}%)
                  </Typography>
                )}
              </Grid>
            )}          
          </>
        ))}
        {candidates.length > 2 && (
          <Grid item xs={12} textAlign="center" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <Box component="span" sx={{ color: 'grey.500', mr: 0.5 }}>■</Box>
              Undecided: {undecidedVotes} ({undecidedPercentage.toFixed(1)}%)
              {projection && projectionUndecidedPercentage !== undefined && (
                <Typography 
                  component="span" 
                  variant="caption" 
                  sx={{ ml: 1, color: 'text.secondary', fontStyle: 'italic' }}
                >
                  (Proj: {projectionUndecidedPercentage.toFixed(1)}%)
                </Typography>
              )}
            </Typography>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

export default Comparison;
